import DOMPurify from "dompurify";
import React, { ReactElement } from "react";
import { jsx } from "slate-hyperscript";
import { FaPlay } from "react-icons/fa";
import Styles from "../Editor.module.scss";
import {
    BlockHandler, ElementSettings, ElementType, StateElement, TextElement,
} from "./base";
import {
    ALIGN_CLASS,
    Align, BORDER_CLASS, BUTTON_CLASS, MediaSettings, MediaToolbar, SHADOW_CLASS,
} from "./media-toolbar";

export interface VideoSettingsTrack extends ElementSettings {
    url: string;
    label: string;
    sourceLanguage: string;
    kind: string;
}

export interface VideoSettings extends MediaSettings {
    src?: string;
    mimeType?: string;
    track?: VideoSettingsTrack[];
    shouldAutoPlay?: boolean;
}

BlockHandler.register(ElementType.VIDEO, {
    nodeTypes: ["VIDEO"],
    toBlock: (child: ReactElement, settings: VideoSettings, editorSettings) => {
        const setValue = (value: VideoSettings) => {
            editorSettings.onUpdate({
                type: ElementType.VIDEO,
                [ElementType.VIDEO]: {
                    ...settings,
                    ...value,
                },
                children: [{
                    text: "",
                } as TextElement],
            } as StateElement);
        };

        const styles: {width?: number; height?: number} = {};
        if (settings.width) {
            styles.width = typeof settings.width === "string" ? parseInt(settings.width, 10) : settings.width;
        }
        if (settings.height) {
            styles.height = typeof settings.height === "string" ? parseInt(settings.height, 10) : settings.width;
        }

        return (
            <div
                contentEditable={false}
                className={`position-relative d-block ${Styles["editable-image"]} ${ALIGN_CLASS[settings.align ?? Align.LEFT]}`}
                style={styles}
            >
                {!editorSettings.isReadOnly && (
                    <MediaToolbar settings={settings} onSetValue={setValue} onRemove={editorSettings.onRemove}>
                        <button
                            className={`btn btn-sm text-white btn-sm py-2 px-2 ms-1 ${
                                settings.shouldAutoPlay ? BUTTON_CLASS.md : ""}`}
                            type="button"
                            title="Should autoplay "
                            onClick={() => setValue({ shouldAutoPlay: !settings.shouldAutoPlay })}
                        >
                            <FaPlay />
                        </button>

                    </MediaToolbar>
                )}
                {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                <video
                    className={`w-100 h-100 bg-primary ${
                        settings.shadow ? SHADOW_CLASS[settings.shadow] : ""} ${
                        settings.border ? BORDER_CLASS[settings.border] : ""}`}
                    controls
                    autoPlay={settings.shouldAutoPlay && editorSettings.isReadOnly}
                >
                    <source src={settings.src} type={settings.mimeType} />
                    {settings.track?.map(track => (
                        <track
                            key={track.url}
                            src={track.url}
                            kind={track.kind}
                            srcLang={track.sourceLanguage}
                            label={track.label}
                        />
                    ))}
                </video>
                {child}
            </div>
        );
    },

    serialize: (_child, settings: VideoSettings | undefined) => (
        `<video `
                    + `width="${settings?.width?.toString() ?? ""}" `
                    + `height="${settings?.height?.toString() ?? ""}" `
                    + `shadow="${settings?.shadow?.toString() ?? ""}" `
                    + `border="${settings?.border?.toString() ?? ""}" `
                    + `align="${settings?.align?.toString() ?? ""}"`
                    + `autoplay="${settings?.shouldAutoPlay?.toString() ?? ""}"`
                    + `><source src="${DOMPurify().sanitize(settings?.src ?? "")}" `
                    + `type="${DOMPurify().sanitize(settings?.mimeType ?? "")}"></video>`
    ),
    deserialize: (children, settings) => {
        const source = settings?.getElementsByTagName("source")[0];
        return jsx("element", {
            type: ElementType.VIDEO,
            [ElementType.VIDEO]: {
                src: source?.getAttribute("src"),
                mimeType: source?.getAttribute("type"),
                shouldAutoPlay: settings?.getAttribute("autoplay"),
                height: settings?.getAttribute("height"),
                width: settings?.getAttribute("width"),
                shadow: settings?.getAttribute("shadow"),
                border: settings?.getAttribute("border"),
                align: settings?.getAttribute("align"),
            },
        }, children);
    },

});
